import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { createCategoryDefect } from "../../services/assessmentApi";
import { fetchCategories } from "../../services/deviceApi";
import {
  fetchCatalogDefects,
  fetchDefectGroups,
  toggleDefectStatus,
  updateDefect,
  deleteCatalogDefect,
  deleteUngroupedCatalogDefects,
} from "../../services/defectApi";
import { fetchDefectGallery } from "../../services/defectGalleryApi";
import Pagination from "../../components/admin/Pagination";
import TableScroll from "../../components/TableScroll";
import "../../styles/admin-common.css";

const PAGE_SIZE = 500;

const emptyDefect = {
  title: "",
  groupName: "",
  category: "SCREEN",
  priceImpact: "",
  icon: "",
  galleryImageId: null,
  isActive: true,
};

const choiceTypeForCategory = (category) => {
  if (["SCREEN", "BATTERY", "CAMERA"].includes(category)) return "SINGLE_CHOICE";
  return "MULTIPLE_CHOICE";
};

const Defects = () => {
  const [searchParams] = useSearchParams();
  const legacyDeviceId = searchParams.get("deviceId");

  const [categories, setCategories] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [groups, setGroups] = useState([]);
  const [productTypeId, setProductTypeId] = useState(searchParams.get("categoryId") || "");
  const [defects, setDefects] = useState([]);
  const [form, setForm] = useState(emptyDefect);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [filterGroupName, setFilterGroupName] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [cleaningUngrouped, setCleaningUngrouped] = useState(false);

  const groupOptions = useMemo(() => {
    const sorted = [...groups].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    if (form.groupName && !sorted.some((g) => g.name === form.groupName)) {
      return [
        {
          id: `legacy-${form.groupName}`,
          name: form.groupName,
          category: form.category,
          description: "Current value (reassign to a group)",
          displayOrder: -1,
          isActive: false,
        },
        ...sorted,
      ];
    }
    return sorted;
  }, [groups, form.groupName, form.category]);

  const selectedGroup = groupOptions.find((g) => g.name === form.groupName);

  const galleryOptions = useMemo(() => {
    const list = !form.category
      ? gallery
      : gallery.filter((g) => !g.category || g.category === form.category);
    if (form.galleryImageId && !list.some((g) => String(g.id) === String(form.galleryImageId))) {
      const orphan = gallery.find((g) => String(g.id) === String(form.galleryImageId));
      if (orphan) return [orphan, ...list];
      if (form.icon) {
        return [
          {
            id: form.galleryImageId,
            name: "Current image",
            imageUrl: form.icon,
            category: form.category,
          },
          ...list,
        ];
      }
    }
    return list;
  }, [gallery, form.category, form.galleryImageId, form.icon]);

  useEffect(() => {
    fetchCategories().then((cats) => {
      setCategories(cats);
      setProductTypeId((current) => {
        if (current) return current;
        if (cats.length === 0) return current;
        const mobile = cats.find((c) => c.name?.toLowerCase() === "mobile") || cats[0];
        return String(mobile.id);
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!productTypeId) {
      setGallery([]);
      setGroups([]);
      return;
    }
    fetchDefectGallery(true, productTypeId).then(setGallery).catch(() => setGallery([]));
    // Include inactive so edit can still resolve existing group names.
    fetchDefectGroups(productTypeId, { includeInactive: true })
      .then((data) => setGroups((data || []).filter((g) => g.isActive !== false)))
      .catch(() => setGroups([]));
  }, [productTypeId]);

  useEffect(() => {
    setCurrentPage(0);
    setFilterGroupName("");
    setCollapsedGroups({});
  }, [productTypeId]);

  const loadDefects = async ({ pageOverride, silent = false } = {}) => {
    if (!productTypeId) return;
    const page = pageOverride ?? currentPage;
    if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const data = await fetchCatalogDefects(productTypeId, {
        page,
        size: PAGE_SIZE,
        includeInactive: true,
      });
      setDefects(data.content || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalElements(data.pagination?.totalElements ?? (data.content?.length || 0));
    } catch (err) {
      setError(err.message);
      if (!silent) {
        setDefects([]);
        setTotalPages(1);
        setTotalElements(0);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadDefects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypeId, currentPage]);

  const groupOrderIndex = useMemo(() => {
    const map = new Map();
    [...groups]
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .forEach((g, i) => {
        if (g.name) map.set(g.name.trim().toLowerCase(), g.displayOrder ?? i * 10);
      });
    return map;
  }, [groups]);

  const defectsByGroup = useMemo(() => {
    const filtered = filterGroupName
      ? defects.filter((d) => (d.groupName || "") === filterGroupName)
      : defects;

    const buckets = new Map();
    for (const d of filtered) {
      const name = (d.groupName || "").trim() || "Ungrouped";
      if (!buckets.has(name)) buckets.set(name, []);
      buckets.get(name).push(d);
    }

    const sections = [...buckets.entries()].map(([groupName, items]) => {
      const meta = groups.find((g) => g.name === groupName);
      const order = groupOrderIndex.get(groupName.toLowerCase());
      return {
        groupName,
        description: meta?.description || "",
        category: meta?.category || items[0]?.category || "",
        selectionType: meta?.selectionType || "",
        displayOrder: order != null ? order : 9990,
        defects: [...items].sort((a, b) =>
          (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" })
        ),
      };
    });

    sections.sort((a, b) => {
      const byOrder = a.displayOrder - b.displayOrder;
      if (byOrder !== 0) return byOrder;
      return a.groupName.localeCompare(b.groupName);
    });
    return sections;
  }, [defects, filterGroupName, groups, groupOrderIndex]);

  const toggleGroupCollapsed = (groupName) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const handleGroupSelect = (groupName) => {
    const group = groupOptions.find((g) => g.name === groupName);
    setForm((prev) => ({
      ...prev,
      groupName,
      category: group?.category || prev.category,
    }));
  };

  const handleGallerySelect = (galleryImageId) => {
    if (!galleryImageId) {
      setForm((prev) => ({ ...prev, icon: "", galleryImageId: null }));
      return;
    }
    const picked =
      gallery.find((g) => String(g.id) === String(galleryImageId))
      || galleryOptions.find((g) => String(g.id) === String(galleryImageId));
    setForm((prev) => ({
      ...prev,
      icon: picked?.imageUrl || prev.icon || "",
      galleryImageId: picked?.id != null ? picked.id : Number(galleryImageId),
    }));
  };

  const resetForm = () => {
    setForm(emptyDefect);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!form.title.trim()) {
      setError("Defect name is required");
      return;
    }
    if (!form.groupName) {
      setError("Select a group");
      return;
    }
    const impact = Number(form.priceImpact);
    if (Number.isNaN(impact)) {
      setError("Impact % must be a number");
      return;
    }
    const category = selectedGroup?.category || form.category || "SCREEN";
    const payload = {
      title: form.title.trim(),
      description: form.title.trim(),
      category,
      groupName: form.groupName,
      categoryType: choiceTypeForCategory(category),
      priceImpact: impact,
      icon: form.icon || "",
      galleryImageId: form.galleryImageId || null,
      severity: 1,
      isActive: form.isActive !== false,
    };

    setSaving(true);
    try {
      if (editingId) {
        const updated = await updateDefect(editingId, { ...payload, id: editingId });
        setDefects((prev) =>
          prev.map((d) =>
            d.id === editingId
              ? {
                  ...d,
                  ...(updated && typeof updated === "object" ? updated : {}),
                  title: payload.title,
                  groupName: payload.groupName,
                  category: payload.category,
                  priceImpact: payload.priceImpact,
                  icon: payload.icon,
                  galleryImageId: payload.galleryImageId,
                  isActive: payload.isActive,
                }
              : d
          )
        );
        setSuccess("Defect updated");
      } else {
        const created = await createCategoryDefect(productTypeId, payload);
        if (created && created.id != null) {
          setDefects((prev) => [
            {
              canDelete: true,
              usageCount: 0,
              ...created,
              title: created.title || payload.title,
              groupName: created.groupName || payload.groupName,
              category: created.category || payload.category,
              priceImpact: created.priceImpact ?? payload.priceImpact,
              icon: created.icon || payload.icon,
              galleryImageId: created.galleryImageId ?? payload.galleryImageId,
              isActive: created.isActive !== false,
            },
            ...prev,
          ]);
          setTotalElements((n) => n + 1);
        } else {
          await loadDefects({ silent: true });
        }
        setSuccess("Defect created");
      }
      resetForm();
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (defect) => {
    const nextActive = !defect.isActive;
    setDefects((prev) =>
      prev.map((d) => (d.id === defect.id ? { ...d, isActive: nextActive } : d))
    );
    try {
      await toggleDefectStatus(defect.id);
    } catch (err) {
      setDefects((prev) =>
        prev.map((d) => (d.id === defect.id ? { ...d, isActive: defect.isActive } : d))
      );
      setError(err.message);
    }
  };

  const knownGroupNames = useMemo(() => {
    const set = new Set();
    groups.forEach((g) => {
      if (g.name) set.add(g.name.trim().toLowerCase());
    });
    return set;
  }, [groups]);

  const isOrphanGroup = (groupName) => {
    const name = (groupName || "").trim();
    if (!name || name === "Ungrouped") return true;
    if (knownGroupNames.size === 0) return false;
    return !knownGroupNames.has(name.toLowerCase());
  };

  const ungroupedUnusedCount = useMemo(() => {
    return defects.filter((d) => {
      const name = (d.groupName || "").trim();
      return !name || (knownGroupNames.size > 0 && !knownGroupNames.has(name.toLowerCase()));
    }).length;
  }, [defects, knownGroupNames]);

  const handleDeleteUngrouped = async () => {
    if (!productTypeId || ungroupedUnusedCount === 0) return;
    if (
      !window.confirm(
        `Delete ${ungroupedUnusedCount} unused defect(s) with no valid group?\n\nDefects used on orders are skipped.`
      )
    ) {
      return;
    }
    setError("");
    setSuccess("");
    setCleaningUngrouped(true);
    try {
      const deleted = await deleteUngroupedCatalogDefects(productTypeId);
      setSuccess(
        deleted > 0
          ? `Deleted ${deleted} unused ungrouped defect${deleted === 1 ? "" : "s"}`
          : "No unused ungrouped defects to delete"
      );
      await loadDefects({ silent: true });
    } catch (err) {
      setError(err.message || "Cleanup failed");
    } finally {
      setCleaningUngrouped(false);
    }
  };

  const handleDelete = async (defect) => {
    const usedOnOrders = defect.usageCount != null && defect.usageCount > 0;
    const confirmMsg = usedOnOrders
      ? `Delete "${defect.title}" from central catalog and all models?\n\nUsed on ${defect.usageCount} order(s) — those orders keep their own defect snapshot. This cannot be undone.`
      : `Delete "${defect.title}" from central catalog and all device models? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setError("");
    setSuccess("");
    const previous = defects;
    setDefects((prev) => prev.filter((d) => d.id !== defect.id));
    setTotalElements((n) => Math.max(0, n - 1));
    if (editingId === defect.id) resetForm();
    try {
      await deleteCatalogDefect(defect.id);
      setSuccess("Defect hard-deleted from central catalog and all models");
    } catch (err) {
      setDefects(previous);
      setTotalElements(previous.length);
      setError(err.message);
    }
  };

  const startEdit = (d) => {
    setError("");
    setSuccess("");
    setEditingId(d.id);
    setForm({
      title: d.title || "",
      groupName: d.groupName || "",
      category: d.category || "SCREEN",
      priceImpact: String(d.priceImpact ?? d.defaultPriceImpact ?? ""),
      icon: d.icon || "",
      galleryImageId: d.galleryImageId || null,
      isActive: d.isActive !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const productTypeName = categories.find((c) => String(c.id) === String(productTypeId))?.name || "—";

  if (legacyDeviceId) {
    return <Navigate to={`/models/${legacyDeviceId}/defects`} replace />;
  }

  return (
    <div className="admin-page">
      <h2>Central defects</h2>
      <p className="subtitle">
        Create the real defects here: <strong>name</strong> + <strong>group</strong> + <strong>image</strong> +{" "}
        <strong>impact %</strong>. Listed <strong>by group</strong> (admin display order) so you can edit one section at a time.
        {" "}Groups: <Link to={`/defect-groups?categoryId=${productTypeId || ""}`}>Defect groups</Link>
        {" · "}Images: <Link to="/defect-gallery">Defect gallery</Link>
      </p>
      {error && <p className="error-msg">{error}</p>}
      {success && <p className="subtitle" style={{ color: "#0a7a4b" }}>{success}</p>}

      <div className="admin-filters">
        <label className="field-label">
          Product type
          <select value={productTypeId} onChange={(e) => setProductTypeId(e.target.value)}>
            <option value="">Select type</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        {productTypeId && groups.length > 0 && (
          <label className="field-label">
            Filter by group
            <select
              value={filterGroupName}
              onChange={(e) => setFilterGroupName(e.target.value)}
            >
              <option value="">All groups</option>
              {[...groups]
                .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                .map((g) => (
                  <option key={g.id} value={g.name}>
                    {g.name} ({g.defectCount ?? "—"})
                  </option>
                ))}
            </select>
          </label>
        )}
      </div>

      {productTypeId && (
        <>
          <div className="admin-card">
            <h3>{editingId ? `Edit defect #${editingId}` : `Add defect — ${productTypeName}`}</h3>
            {groupOptions.length === 0 && (
              <p className="error-msg">
                No groups yet. Add groups first on{" "}
                <Link to={`/defect-groups?categoryId=${productTypeId}`}>Defect groups</Link>.
              </p>
            )}
            <form className="admin-form admin-form-grid" onSubmit={handleSubmit}>
              <label className="field-label span-2">
                Defect name
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Cracked screen"
                  required
                />
              </label>
              <label className="field-label span-2">
                Group
                <select
                  value={form.groupName}
                  onChange={(e) => handleGroupSelect(e.target.value)}
                  required
                >
                  <option value="">Select group</option>
                  {groupOptions.map((g) => (
                    <option key={g.id} value={g.name}>
                      {g.name}{g.description ? ` — ${g.description}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field-label">
                Impact %
                <input
                  type="number"
                  step="any"
                  value={form.priceImpact}
                  onChange={(e) => setForm({ ...form, priceImpact: e.target.value })}
                  required
                />
              </label>
              <label className="field-label">
                Image (from gallery)
                <select
                  value={form.galleryImageId || ""}
                  onChange={(e) => handleGallerySelect(e.target.value)}
                >
                  <option value="">Select image</option>
                  {galleryOptions.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </label>
              {gallery.length === 0 && (
                <small className="field-hint span-2">
                  No gallery images. <Link to="/defect-gallery">Upload in Defect gallery</Link>
                </small>
              )}
              {form.icon ? (
                <img src={form.icon} alt="Selected defect" className="image-upload-preview span-2" />
              ) : null}
              <div className="form-actions span-2">
                <button type="submit" className="admin-btn" disabled={saving || groupOptions.length === 0}>
                  {saving ? "Saving…" : editingId ? "Update" : "Save"}
                </button>
                {editingId && (
                  <button type="button" className="admin-btn secondary" onClick={resetForm} disabled={saving}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {loading && defects.length === 0 ? (
            <div className="admin-loading">Loading defects</div>
          ) : (
            <>
              {defects.length > 0 && (
                <div className="admin-filters" style={{ marginBottom: 12 }}>
                  <p className="subtitle" style={{ margin: 0 }}>
                    {filterGroupName
                      ? `${defectsByGroup[0]?.defects.length ?? 0} defect${
                          (defectsByGroup[0]?.defects.length ?? 0) === 1 ? "" : "s"
                        } in “${filterGroupName}”`
                      : `${totalElements} defect${totalElements === 1 ? "" : "s"} across ${
                          defectsByGroup.length
                        } group${defectsByGroup.length === 1 ? "" : "s"}`}
                  </p>
                  {ungroupedUnusedCount > 0 && (
                    <button
                      type="button"
                      className="admin-btn danger"
                      disabled={cleaningUngrouped}
                      onClick={handleDeleteUngrouped}
                    >
                      {cleaningUngrouped
                        ? "Deleting…"
                        : `Delete ${ungroupedUnusedCount} ungrouped`}
                    </button>
                  )}
                </div>
              )}
              {defects.length === 0 ? (
                <p className="empty-msg">No defects for this product type</p>
              ) : defectsByGroup.length === 0 ? (
                <p className="empty-msg">No defects in this group</p>
              ) : (
                defectsByGroup.map((section) => {
                  const collapsed = !!collapsedGroups[section.groupName];
                  const orphan = isOrphanGroup(section.groupName);
                  return (
                    <div className="defect-group-block" key={section.groupName}>
                      <button
                        type="button"
                        className="defect-group-header"
                        onClick={() => toggleGroupCollapsed(section.groupName)}
                      >
                        <span className="defect-group-title">
                          <span className="defect-group-chevron">{collapsed ? "▸" : "▾"}</span>
                          {section.groupName}
                          {orphan ? (
                            <span className="defect-group-count" title="Not linked to a Defect group">
                              no group
                            </span>
                          ) : null}
                          <span className="defect-group-count">{section.defects.length}</span>
                        </span>
                        <span className="defect-group-meta">
                          {orphan
                            ? "Not associated with any Defect group — safe to delete if unused"
                            : [
                                section.category || "",
                                section.selectionType === "SINGLE_CHOICE"
                                  ? "Pick one"
                                  : section.selectionType === "MULTIPLE_CHOICE"
                                    ? "Multi"
                                    : "",
                                section.description || "",
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                        </span>
                      </button>
                      {!collapsed && (
                        <TableScroll>
                          <table className="admin-table">
                            <thead>
                              <tr>
                                <th>Defect name</th>
                                <th>Impact %</th>
                                <th>Image</th>
                                <th>Status</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {section.defects.map((d) => (
                                  <tr key={d.id}>
                                    <td>{d.title}</td>
                                    <td>{d.priceImpact}%</td>
                                    <td>
                                      {d.icon ? (
                                        <img
                                          src={d.icon}
                                          alt=""
                                          style={{ width: 36, height: 36, objectFit: "contain" }}
                                        />
                                      ) : "—"}
                                    </td>
                                    <td>{d.isActive ? "Active" : "Inactive"}</td>
                                    <td className="actions-cell">
                                      <button
                                        type="button"
                                        className="admin-btn secondary"
                                        onClick={() => startEdit(d)}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        className="admin-btn danger"
                                        title={
                                          d.usageCount > 0
                                            ? `Used on ${d.usageCount} order(s) — orders keep snapshots; hard-delete from central/models`
                                            : "Hard-delete from central catalog and all models"
                                        }
                                        onClick={() => handleDelete(d)}
                                      >
                                        Delete
                                      </button>
                                      <button
                                        type="button"
                                        className="admin-btn"
                                        onClick={() => handleToggle(d)}
                                      >
                                        {d.isActive ? "Deactivate" : "Activate"}
                                      </button>
                                    </td>
                                  </tr>
                              ))}
                            </tbody>
                          </table>
                        </TableScroll>
                      )}
                    </div>
                  );
                })
              )}
              {totalPages > 1 && (
                <Pagination page={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Defects;
